"use client";

import { motion } from "framer-motion";
import { FaCode, FaMobile, FaCloud, FaCogs } from "react-icons/fa";

const services = [
  {
    icon: <FaCode className="text-4xl" />,
    title: "Web Development",
    description: "Custom web applications and responsive websites built with the latest technologies.",
  },
  {
    icon: <FaMobile className="text-4xl" />,
    title: "Mobile Development",
    description: "Native and cross-platform mobile applications for iOS and Android.",
  },
  {
    icon: <FaCloud className="text-4xl" />,
    title: "Cloud Solutions",
    description: "Scalable cloud infrastructure and migration services for your business.",
  },
  {
    icon: <FaCogs className="text-4xl" />,
    title: "IT Consulting",
    description: "Strategic technology consulting to help you make informed decisions.",
  },
];

export default function Services() {
  return (
    <section className="py-20 bg-gradient-to-br from-white via-gray-50 to-white relative overflow-hidden">
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <motion.div
          className="absolute -right-1/4 -top-1/4 w-1/2 h-1/2 rounded-full bg-gradient-to-br from-blue-200 to-indigo-200"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute -left-1/4 -bottom-1/4 w-1/2 h-1/2 rounded-full bg-gradient-to-tr from-indigo-200 to-purple-200"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "150px" }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-purple-500 mx-auto mb-8"
          />
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-indigo-600 via-blue-600 to-purple-600 bg-clip-text text-transparent"
          >
            Our Services
          </motion.h2>
          <motion.p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            We offer a comprehensive range of IT services to help your business grow and succeed in the digital world.
          </motion.p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
              }
            }
          }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={{
                hidden: { opacity: 0, y: 50 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
              }}
              className="group relative bg-gradient-to-br from-white/90 via-white/70 to-white/50 backdrop-blur-lg p-8 rounded-3xl
                overflow-hidden hover:shadow-[0_20px_60px_-15px_rgba(79,70,229,0.3)] transition-all duration-500
                border border-gray/30 hover:border-indigo-200/50 flex flex-col h-full
                hover:translate-y-[-5px] hover:scale-[1.02] "
              >
                <div 
                className="relative flex items-center justify-center text-indigo-600 mb-8
                  rounded-2xl w-full p-8 bg-gradient-to-br from-indigo-50/80 to-white/80
                  group-hover:from-indigo-100/80 group-hover:to-purple-50/80 transition-all duration-500
                  shadow-[inset_0_0_0_1px_rgba(79,70,229,0.1)] group-hover:shadow-[inset_0_0_0_2px_rgba(79,70,229,0.2)]"
              >
                <div className="relative z-10 transform group-hover:scale-125 transition-transform duration-500 group-hover:rotate-[360deg]">
                  {service.icon}
                </div>
              </div>

              <div className="flex flex-col flex-grow space-y-4">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent
                  transition-colors duration-300 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300 leading-relaxed flex-grow">
                  {service.description}
                </p>
                <motion.a
                  href={`/services/${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="inline-flex items-center text-indigo-600 opacity-0 group-hover:opacity-100 
                    transition-all duration-300 hover:text-indigo-800 font-semibold
                    bg-gradient-to-r from-indigo-50/50 to-transparent
                    py-3 px-6 rounded-xl self-start"
                  whileHover={{ x: 10 }}
                >
                  Learn More
                  <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </motion.a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}